import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PATH } from "routes/paths";
import {
  ambassadorApi,
  getAmbassadorToken,
  clearAmbassadorToken,
  type ReferralAmbassadorStatsBucket,
} from "api/ambassadorApi";

type Tab = "daily" | "monthly";

// 레퍼럴 앰버서더(인플루언서) 성과 대시보드 — /with/:code 링크의 가입자/결제자/환불자를 total·일별·월별로 본다.
// 토큰 만료(401)는 ambassadorAxios 인터셉터가 로그인 리다이렉트로 처리한다.
export default function AmbassadorDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("daily");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // 내 링크 풀 URL 복사 (예: https://distancing.im/with/CODE)
  const copyLink = async (code: string) => {
    const link = `${window.location.origin}/with/${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch (e) {
      // clipboard 미지원 브라우저 — 링크 텍스트가 select-all 이라 수동 복사 가능
    }
  };

  const hasToken = !!getAmbassadorToken();

  const { data, isLoading, isError } = useQuery(
    ["referral-ambassador-dashboard"],
    () => ambassadorApi.dashboard(),
    { enabled: hasToken, retry: false, refetchOnWindowFocus: false }
  );

  if (!hasToken) {
    return <Navigate to={PATH.ambassadorLogin} replace />;
  }

  const handleLogout = () => {
    clearAmbassadorToken();
    navigate(PATH.ambassadorLogin, { replace: true });
  };

  const result = data?.code === 0 ? data.result : undefined;
  const failed = isError || (data !== undefined && data.code !== 0);
  const buckets: ReferralAmbassadorStatsBucket[] =
    (tab === "daily" ? result?.daily : result?.monthly) ?? [];

  return (
    <div className="w-full h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-[720px] px-5 py-10">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              디스턴싱 앰버서더 대시보드
            </h1>
            {result && (
              <div className="mt-1 flex flex-col gap-1">
                {result.ambassadorName && (
                  <p className="text-sm text-gray-500">
                    {result.ambassadorName}
                  </p>
                )}
                {result.codes.map((code) => (
                  <div
                    key={code}
                    className="flex items-center gap-2 text-sm text-gray-500"
                  >
                    <span className="select-all break-all">
                      {`${window.location.origin}/with/${code}`}
                    </span>
                    <button
                      className="shrink-0 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600"
                      onClick={() => copyLink(code)}
                    >
                      {copiedCode === code ? "복사됨" : "링크 복사"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <AccountMenu email={result?.email} onLogout={handleLogout} />
        </div>

        {isLoading && (
          <p className="mt-10 text-sm text-gray-500">불러오는 중...</p>
        )}
        {failed && (
          <p className="mt-10 text-sm text-red-500">
            데이터를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
          </p>
        )}

        {result && (
          <>
            <div
              className={`mt-6 grid gap-3 ${
                result.total.settlementAmount != null
                  ? "grid-cols-4"
                  : "grid-cols-3"
              }`}
            >
              <SummaryCard label="총 가입자" value={result.total.signupCount} />
              <SummaryCard label="총 결제자" value={result.total.payerCount} />
              <SummaryCard label="총 환불자" value={result.total.refundCount} />
              {result.total.settlementAmount != null && (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-5">
                  <p className="text-sm text-gray-500">총 정산액</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {result.total.settlementAmount.toLocaleString()}원
                  </p>
                </div>
              )}
            </div>

            <div className="mt-8 flex gap-2">
              <TabButton
                active={tab === "daily"}
                label="일별"
                onClick={() => setTab("daily")}
              />
              <TabButton
                active={tab === "monthly"}
                label="월별"
                onClick={() => setTab("monthly")}
              />
            </div>

            <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-500">
                    <th className="px-4 py-3 font-medium">
                      {tab === "daily" ? "날짜" : "월"}
                    </th>
                    <th className="px-4 py-3 text-right font-medium">가입자</th>
                    <th className="px-4 py-3 text-right font-medium">결제자</th>
                    <th className="px-4 py-3 text-right font-medium">환불자</th>
                    {tab === "monthly" && (
                      <th className="px-4 py-3 text-right font-medium">
                        정산액
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {buckets.length === 0 && (
                    <tr>
                      <td
                        className="px-4 py-6 text-center text-gray-400"
                        colSpan={tab === "monthly" ? 5 : 4}
                      >
                        아직 집계된 데이터가 없어요
                      </td>
                    </tr>
                  )}
                  {buckets.map((bucket) => (
                    <tr
                      key={bucket.period}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-4 py-3 text-gray-900">
                        {bucket.period}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {bucket.signupCount}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {bucket.payerCount}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {bucket.refundCount}
                      </td>
                      {tab === "monthly" && (
                        <td className="px-4 py-3 text-right text-gray-900">
                          {bucket.settlementAmount != null
                            ? `${bucket.settlementAmount.toLocaleString()}원`
                            : "-"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-xs text-gray-400">
              가입자는 링크로 유입되어 코드가 연결된 사용자, 결제자는 링크 진입
              3일 내 결제로 기여가 확정된 사용자, 환불자는 그중 결제를 취소한
              사용자 기준이에요.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">
        {value.toLocaleString()}명
      </p>
    </div>
  );
}

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className={`rounded-full px-4 py-1.5 text-sm font-medium ${
        active
          ? "bg-gray-900 text-white"
          : "bg-white text-gray-500 border border-gray-200"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// 계정 관리 메뉴 — 로그인 이메일 표시 · 비밀번호 변경 · 로그아웃
function AccountMenu({
  email,
  onLogout,
}: {
  email?: string | null;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  return (
    <div className="relative shrink-0">
      <button
        className="text-sm text-gray-500 underline"
        onClick={() => setOpen(!open)}
      >
        계정 관리
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-2 w-56 rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
          {email && (
            <p className="break-all px-2 py-1.5 text-xs text-gray-400">
              {email}
            </p>
          )}
          <button
            className="block w-full rounded-lg px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setChangingPassword(true);
              setOpen(false);
            }}
          >
            비밀번호 변경
          </button>
          <button
            className="block w-full rounded-lg px-2 py-2 text-left text-sm text-red-500 hover:bg-gray-50"
            onClick={onLogout}
          >
            로그아웃
          </button>
        </div>
      )}
      {changingPassword && (
        <PasswordChangePanel onClose={() => setChangingPassword(false)} />
      )}
    </div>
  );
}

function PasswordChangePanel({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch =
    newPasswordConfirm.length > 0 && newPassword !== newPasswordConfirm;
  const canSubmit =
    !submitting &&
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    newPassword === newPasswordConfirm;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await ambassadorApi.changePassword(
        currentPassword,
        newPassword
      );
      if (res.code === 0) {
        setDone(true);
        return;
      }
      setMessage(res.message || "잠시 후 다시 시도해 주세요");
    } catch (e) {
      setMessage("잠시 후 다시 시도해 주세요");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClassName =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-900";

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 px-5">
      <div className="w-full max-w-[360px] rounded-xl bg-white p-5">
        <p className="text-base font-bold text-gray-900">비밀번호 변경</p>
        {done ? (
          <>
            <p className="mt-3 text-sm text-gray-600">비밀번호가 변경됐어요.</p>
            <button
              className="mt-4 w-full rounded-lg bg-gray-900 py-2.5 text-sm font-semibold text-white"
              onClick={onClose}
            >
              확인
            </button>
          </>
        ) : (
          <>
            <div className="mt-3 flex flex-col gap-2">
              <input
                className={inputClassName}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="현재 비밀번호"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <input
                className={inputClassName}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="새 비밀번호 (8자 이상)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <input
                className={inputClassName}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="새 비밀번호 확인"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
              />
              <label className="flex items-center gap-1.5 text-sm text-gray-500">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                />
                비밀번호 표시
              </label>
            </div>
            {mismatch && (
              <p className="mt-2 text-sm text-red-500">
                새 비밀번호가 일치하지 않아요.
              </p>
            )}
            {message && <p className="mt-2 text-sm text-red-500">{message}</p>}
            <div className="mt-4 flex gap-2">
              <button
                className="flex-1 rounded-lg bg-gray-900 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                disabled={!canSubmit}
                onClick={submit}
              >
                {submitting ? "변경 중..." : "변경"}
              </button>
              <button
                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm text-gray-600"
                onClick={onClose}
              >
                취소
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
